/**
 * Bước 3 của phần chọn dịch vụ — kiểu ảnh (concept) của gói Profile.
 *
 * Khách chọn bằng ẢNH chứ không bằng chữ (CEO chốt 21/08/2026): tên concept
 * như "Vị Thế" hay "Biểu Tượng" không nói được gì cho người chưa từng chụp,
 * nhìn ảnh mẫu mới quyết được.
 *
 * KHÔNG bắt buộc — bỏ trống thì ekip hỏi sau, còn hơn bắt khách quyết ngay
 * rồi bỏ dở đơn. Vì vậy luôn có nút bỏ chọn khi đã chọn.
 *
 * Nguồn ảnh và danh sách đều từ ERP (trang Concept & Trang phục + allow-list
 * concept của gói), máy chủ đã lọc theo miền của cơ sở — chỗ này chỉ vẽ.
 */

import { useState } from "react";
import { Camera, Check, X } from "lucide-react";
import type { PublicBookingConcept } from "./api";

type Props = {
  concepts: readonly PublicBookingConcept[];
  selectedId: string;
  onSelect: (conceptId: string) => void;
};

export function ConceptCards({ concepts, selectedId, onSelect }: Props) {
  if (concepts.length === 0) return null;
  const selected = concepts.find((concept) => concept.id === selectedId) ?? null;

  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="text-sm font-medium text-muted-foreground">
          3. Kiểu ảnh <span className="font-normal">(không bắt buộc)</span>
        </span>
        {selected && (
          <button
            type="button"
            onClick={() => onSelect("")}
            className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-muted-foreground underline-offset-4 hover:underline"
          >
            <X className="h-3.5 w-3.5" />
            Bỏ chọn
          </button>
        )}
      </div>
      <p className="mb-2.5 text-sm text-muted-foreground">
        Chưa quyết được thì cứ bỏ qua — ekip sẽ tư vấn thêm trước buổi chụp.
      </p>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3" role="group" aria-label="Kiểu ảnh">
        {concepts.map((concept) => (
          <ConceptCard
            key={concept.id}
            concept={concept}
            active={concept.id === selectedId}
            // Bấm lại đúng thẻ đang chọn là bỏ chọn — khách đổi ý không phải
            // đi tìm nút "Bỏ chọn" ở tận trên đầu khối.
            onPick={() => onSelect(concept.id === selectedId ? "" : concept.id)}
          />
        ))}
      </div>
    </div>
  );
}

function ConceptCard({
  concept,
  active,
  onPick,
}: {
  concept: PublicBookingConcept;
  active: boolean;
  onPick: () => void;
}) {
  const [broken, setBroken] = useState(false);
  const showImage = Boolean(concept.imageUrl) && !broken;

  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onPick}
      className={[
        "overflow-hidden rounded-2xl border text-left transition",
        active ? "border-primary bg-primary/5 shadow-sm" : "border-border bg-surface hover:bg-surface-2",
      ].join(" ")}
    >
      <div className="relative aspect-square w-full bg-surface-2">
        {showImage ? (
          <img
            src={concept.imageUrl}
            alt=""
            aria-hidden
            loading="lazy"
            className="h-full w-full object-cover"
            // Ảnh hỏng thì rơi về khung máy ảnh, đừng để ô vỡ giữa lưới.
            onError={() => setBroken(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <Camera className="h-6 w-6" />
          </div>
        )}
        {active && (
          <span className="absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Check className="h-3.5 w-3.5" />
          </span>
        )}
      </div>
      <div className="px-3 py-2.5">
        <p className="text-sm font-semibold leading-5">{concept.name}</p>
        {concept.mood && (
          <p className="mt-0.5 text-xs leading-4 text-muted-foreground">{concept.mood}</p>
        )}
      </div>
    </button>
  );
}
